import _ from 'lodash'
import { reject, flow, mapValues, flatMap, map, uniqBy, isPlainObject, isEmpty, endsWith } from 'lodash/fp'
import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { ModalHeader, ModalBody } from 'reactstrap'
import styled from 'styled-components'

import {
  getAllInvalidField,
  getAppWithId,
  getIsInsureForeignLabour,
  getIsPayerForeignLabour,
  isInvalidFNA,
  isInvalidVulnerableCustomer,
} from 'e-submission/apps/selectors'
import { validateApplication } from 'e-submission/apps/actions'
import EditCard from 'e-submission/components/Card/EditCard'
import Dialog from 'e-submission/components/Dialog'
import { getIcon } from 'e-submission/components/Icon'
import Content from 'e-submission/components/Layout/Content'
import { isSelectedF2F } from 'e-submission/domain/data-model/form/selectors'
import { getToggles } from 'quick-quote/feature-toggles'

const Status = styled.div`
  text-align: center;
  padding: 30px 0;

  div:last-child {
    padding-top: 30px;
  }
`

const Component = styled(({ className }) => {
  const dispatch = useDispatch()
  const history = useHistory()
  const { invalid, invalidFNA, invalidVulnerableCustomer } = useSelector((state) => {
    return {
      invalid: getAllInvalidField(state),
      invalidFNA: isInvalidFNA(state),
      invalidVulnerableCustomer: isInvalidVulnerableCustomer(state),
      isPayerForeignLabour: getIsPayerForeignLabour(state),
      isInsureForeignLabour: getIsInsureForeignLabour(state),
      isF2F: isSelectedF2F(getAppWithId(state)),
    }
  })

  Object.keys(invalid).forEach((key) => {
    if (!invalid[key]?.fields?.length) delete invalid[key]
  })

  if (_.isEmpty(invalid)) return null

  useEffect(() => {
    dispatch(validateApplication())
  }, [])

  const toggle = () => {
    const _invalid = mapValues(
      ({ sections, label }) => ({
        label,
        fields: flow(
          map(({ id, fields, label, heading }) => {
            if (history?.location?.pathname?.includes('lucky-number') && isF2F) {
              let filteredFields = fields
              let isRequireInsuredDocument = false
              let isRequirePayerDocument = false

              if (isInsureForeignLabour) {
                filteredFields = filteredFields.filter((field) => field.id !== 'insured.idNo.passport')
              }

              if (isPayerForeignLabour) {
                filteredFields = filteredFields
                  .filter((field) => field.id !== 'payer.idNo.passport')
                  .filter((field) => field.id !== 'owner.idNo.passport')
              }

              if (id === 'insured-document') {
                filteredFields.forEach((field) => {
                  switch (field.id) {
                    case 'insured.document':
                    case 'insured.official-document':
                      isRequireInsuredDocument = true
                      break
                  }
                })

                if (isRequireInsuredDocument) {
                  filteredFields = filteredFields.filter((field) => !_.isEmpty(field.label))
                  filteredFields.push({ id: id, label: label, message: [] })
                }
              }

              if (id === 'payer-document') {
                filteredFields.forEach((field) => {
                  switch (field.id) {
                    case 'payer.document':
                    case 'payer.official-document':
                      isRequirePayerDocument = true
                      break
                  }
                })

                if (isRequirePayerDocument) {
                  filteredFields = filteredFields.filter((field) => !_.isEmpty(field.label))
                  filteredFields.push({ id: id, label: label, message: [] })
                }
              }

              return { id, fields: filteredFields }
            }

            if (endsWith(']', id)) {
              return heading
            }

            if (!isEmpty(label)) {
              return label
            }
            const insuredEkyc = fields.filter((field) => field.id == 'remoteSelling.insured.dopaResult')
            const payerEkyc = fields.filter((field) => field.id == 'remoteSelling.payer.dopaResult')
            if (id === 'ekyc' && insuredEkyc.length > 0) {
              return 'กรุณายืนยันตัวตนของผู้เอาประกันภัย'
            } else if (id === 'ekyc' && payerEkyc.length > 0) {
              return 'กรุณายืนยันตัวตนของผู้เอาชำระเบี้ย'
            }

            if (getToggles().ENABLE_EKYC_F2F) {
              const insuredIsRequired = fields.filter((field) => field.id == 'validationEKYC.insured.isRequired')
              const payerIsRequired = fields.filter((field) => field.id == 'validationEKYC.payer.isRequired')

              if (id === 'ekyc' && insuredIsRequired.length > 0) {
                return 'กรุณายืนยันตัวตนของผู้เอาประกันภัย'
              } else if (id === 'ekyc' && payerIsRequired.length > 0) {
                return 'กรุณายืนยันตัวตนของผู้ชำระเบี้ย'
              }
            }

            return { id, fields }
          }),
          reject(isEmpty),
          uniqBy((value) => (isPlainObject(value) ? value.id : value)),
          flatMap((value) => (isPlainObject(value) ? map('label', value.fields) : value))
        )(sections),
      }),
      invalid
    )

    history.push(_.keys(_invalid)[0])
  }

  return (
    <Dialog className={className} isOpen={!_.isEmpty(invalid) || invalidFNA || invalidVulnerableCustomer}>
      <ModalHeader toggle={toggle}>
        <div>กรอกข้อมูลไม่ครบ</div>
      </ModalHeader>
      <ModalBody>
        <Status>
          {getIcon('formInvalid')}
          <div>
            ไม่สามารถสร้างเอกสารได้
            <br />
            <b>กรุณากลับไปแก้ไขข้อมูลด้านล่างให้สมบูรณ์</b>
          </div>
        </Status>
        <Content>
          {_.map(invalid, ({ fields, label }, pageId) => (
            <EditCard key={pageId} title={label} onClick={() => history.push(pageId)}>
              <ul>
                {_.map(fields, (label, index) => (
                  <li key={index}>
                    {getToggles().ENABLE_SHARED_AGENT_SALE_VALIDATION && pageId === 'agent' && label === 'นายหน้า'
                      ? 'กรุณาตรวจสอบสิทธิการขายของตัวแทนอีกท่าน'
                      : label}
                  </li>
                ))}
              </ul>
            </EditCard>
          ))}
          {invalidFNA && (
            <EditCard
              key="FNA"
              title={'แบบสอบถามเพื่อวิเคราะห์ความต้องการซื้อผลิตภัณฑ์ประกันชีวิต'}
              btnText="กลับสู่หน้าแรก"
              onClick={() => history.push('/')}
            >
              <ul>
                <li>ไม่สามารถดำเนินการต่อได้ เนื่องจากไม่มีข้อมูล FNA</li>
                <li>กรุณาเริ่มการขายใหม่</li>
              </ul>
            </EditCard>
          )}
          {invalidVulnerableCustomer && (
            <EditCard
              key="VC"
              title={'แบบสอบถามความเข้าใจ กลุ่มคำถามเปราะบาง'}
              btnText="กลับสู่หน้าแรก"
              onClick={() => history.push('/')}
            >
              <ul>
                <li>ไม่สามารถดำเนินการต่อได้ เนื่องจากไม่มีข้อมูล กลุ่มคำถามเปราะบาง</li>
                <li>กรุณาเริ่มการขายใหม่</li>
              </ul>
            </EditCard>
          )}
        </Content>
      </ModalBody>
    </Dialog>
  )
})`
  .modal-body {
    padding: 0px 0px 32px 0px;
    background: #f5f5f5;
  }
  ul {
    padding-left: 15px;
    margin-bottom: 10px;
    li {
      padding-bottom: 10px;
    }
  }
`

export default Component
